import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { FileCheck, Loader2, Filter, Plus, X, Check, XCircle } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type EvidenceType = 'document' | 'screenshot' | 'audit_log' | 'attestation';
type EvidenceStatus = 'pending' | 'approved' | 'rejected';

interface Evidence {
  id: string;
  framework_id: string;
  framework_name: string;
  requirement_id: string;
  title: string;
  description: string;
  evidence_type: EvidenceType;
  evidence_url: string;
  status: EvidenceStatus;
  uploaded_at: string;
}

interface EvidenceResponse {
  evidence: Evidence[];
}

interface Framework {
  id: string;
  name: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EVIDENCE_TYPES: { value: EvidenceType; label: string }[] = [
  { value: 'document', label: 'Document' },
  { value: 'screenshot', label: 'Screenshot' },
  { value: 'audit_log', label: 'Audit Log' },
  { value: 'attestation', label: 'Attestation' },
];

const FRAMEWORKS: Framework[] = [
  { id: 'eu_ai_act', name: 'EU AI Act' },
  { id: 'nist_ai_rmf', name: 'NIST AI RMF' },
  { id: 'iso_42001', name: 'ISO 42001' },
  { id: 'soc2', name: 'SOC 2' },
  { id: 'gdpr', name: 'GDPR' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function typeBadge(type: EvidenceType) {
  const base = 'px-2 py-0.5 rounded-full text-xs font-medium';
  switch (type) {
    case 'document':
      return <span className={`${base} bg-blue-500/15 text-blue-500`}>Document</span>;
    case 'screenshot':
      return <span className={`${base} bg-purple-500/15 text-purple-500`}>Screenshot</span>;
    case 'audit_log':
      return <span className={`${base} bg-cyan-500/15 text-cyan-500`}>Audit Log</span>;
    case 'attestation':
      return <span className={`${base} bg-emerald-500/15 text-emerald-500`}>Attestation</span>;
  }
}

function statusBadge(status: EvidenceStatus) {
  const base = 'px-2 py-0.5 rounded-full text-xs font-medium';
  switch (status) {
    case 'pending':
      return <span className={`${base} bg-yellow-500/15 text-yellow-500`}>Pending</span>;
    case 'approved':
      return <span className={`${base} bg-green-500/15 text-green-500`}>Approved</span>;
    case 'rejected':
      return <span className={`${base} bg-red-500/15 text-red-500`}>Rejected</span>;
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function EvidencePage() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [filterFramework, setFilterFramework] = useState<string>('all');

  // Form state
  const [frameworkId, setFrameworkId] = useState(FRAMEWORKS[0]!.id);
  const [requirementId, setRequirementId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [evidenceType, setEvidenceType] = useState<EvidenceType>('document');
  const [evidenceUrl, setEvidenceUrl] = useState('');

  const evidenceQuery = useQuery<EvidenceResponse>({
    queryKey: ['governance', 'evidence', filterFramework],
    queryFn: () => api<EvidenceResponse>('governance/models', {
      query: { scope: 'evidence', ...(filterFramework !== 'all' ? { framework: filterFramework } : {}) },
    }),
  });

  const uploadMutation = useMutation({
    mutationFn: (body: { framework_id: string; requirement_id: string; title: string; description: string; evidence_type: EvidenceType; evidence_url: string }) =>
      api<Evidence>('governance/models', { body: { ...body, action: 'upload_evidence' } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['governance', 'evidence'] });
      resetForm();
    },
  });

  const reviewMutation = useMutation({
    mutationFn: (payload: { id: string; status: 'approved' | 'rejected' }) =>
      api<Evidence>('governance/models', { method: 'PUT', body: { scope: 'evidence', ...payload } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['governance', 'evidence'] });
    },
  });

  function resetForm() {
    setShowForm(false);
    setFrameworkId(FRAMEWORKS[0]!.id);
    setRequirementId('');
    setTitle('');
    setDescription('');
    setEvidenceType('document');
    setEvidenceUrl('');
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !requirementId.trim() || !evidenceUrl.trim()) return;
    uploadMutation.mutate({
      framework_id: frameworkId,
      requirement_id: requirementId.trim(),
      title: title.trim(),
      description: description.trim(),
      evidence_type: evidenceType,
      evidence_url: evidenceUrl.trim(),
    });
  }

  const evidence = evidenceQuery.data?.evidence ?? [];

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <FileCheck className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Compliance Evidence</h1>
            <p className="text-muted-foreground mt-1">Collect, manage, and review compliance evidence for governance frameworks.</p>
          </div>
        </div>
        {!showForm && (
          <button type="button" onClick={() => setShowForm(true)} className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 flex-shrink-0">
            <Plus className="h-4 w-4" />
            Upload Evidence
          </button>
        )}
      </div>

      {/* Upload form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="rounded-lg border border-border bg-card p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Upload Evidence</h3>
            <button type="button" onClick={resetForm} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Framework</label>
              <select value={frameworkId} onChange={(e) => setFrameworkId(e.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground">
                {FRAMEWORKS.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Requirement ID</label>
              <input value={requirementId} onChange={(e) => setRequirementId(e.target.value)} required placeholder="e.g. ART-9.1" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Title</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="Evidence title" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Evidence Type</label>
              <select value={evidenceType} onChange={(e) => setEvidenceType(e.target.value as EvidenceType)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground">
                {EVIDENCE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Evidence URL</label>
            <input value={evidenceUrl} onChange={(e) => setEvidenceUrl(e.target.value)} required type="url" placeholder="https://..." className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Describe what this evidence demonstrates..." className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-y" />
          </div>

          {uploadMutation.isError && (
            <div className="rounded-md bg-red-500/10 border border-red-500/30 p-3 text-sm text-red-500">
              {uploadMutation.error instanceof Error ? uploadMutation.error.message : 'Failed to upload evidence.'}
            </div>
          )}

          <div className="flex items-center gap-3">
            <button type="submit" disabled={uploadMutation.isPending || !title.trim() || !requirementId.trim() || !evidenceUrl.trim()} className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
              {uploadMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {uploadMutation.isPending ? 'Uploading...' : 'Upload Evidence'}
            </button>
            <button type="button" onClick={resetForm} className="px-4 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors">Cancel</button>
          </div>
        </form>
      )}

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <select value={filterFramework} onChange={(e) => setFilterFramework(e.target.value)} className="rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground">
          <option value="all">All Frameworks</option>
          {FRAMEWORKS.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      </div>

      {/* Evidence table */}
      {evidenceQuery.isLoading && (
        <div className="rounded-lg border border-border bg-card p-12 flex items-center justify-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <span className="text-muted-foreground">Loading evidence...</span>
        </div>
      )}

      {evidenceQuery.isError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-6 text-center">
          <p className="text-sm text-red-500">
            Failed to load evidence.{' '}
            <button type="button" onClick={() => evidenceQuery.refetch()} className="underline hover:no-underline">Retry</button>
          </p>
        </div>
      )}

      {evidenceQuery.isSuccess && evidence.length === 0 && (
        <div className="rounded-lg border border-border bg-card p-12 text-center text-muted-foreground">
          No evidence uploaded yet. Click "Upload Evidence" to add compliance documentation.
        </div>
      )}

      {evidenceQuery.isSuccess && evidence.length > 0 && (
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <div className="flex items-center gap-4 px-4 py-2 bg-muted/50 border-b border-border text-xs font-medium text-muted-foreground uppercase tracking-wider">
            <span className="flex-1">Title</span>
            <span className="w-24">Framework</span>
            <span className="w-24">Type</span>
            <span className="w-20">Status</span>
            <span className="w-28">Uploaded</span>
            <span className="w-20">Actions</span>
          </div>
          {evidence.map((item) => (
            <div key={item.id} className="flex items-center gap-4 px-4 py-3 border-b border-border last:border-b-0 hover:bg-accent/50">
              <div className="flex-1 min-w-0">
                <a href={item.evidence_url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-foreground hover:text-primary truncate block">{item.title}</a>
                {item.description && <p className="text-xs text-muted-foreground truncate">{item.description}</p>}
              </div>
              <span className="w-24 text-xs text-foreground">{item.framework_name}</span>
              <span className="w-24">{typeBadge(item.evidence_type)}</span>
              <span className="w-20">{statusBadge(item.status)}</span>
              <span className="w-28 text-sm text-muted-foreground">{formatDate(item.uploaded_at)}</span>
              <div className="w-20 flex items-center gap-1">
                {item.status === 'pending' && (
                  <>
                    <button
                      type="button"
                      onClick={() => reviewMutation.mutate({ id: item.id, status: 'approved' })}
                      disabled={reviewMutation.isPending}
                      className="p-1.5 rounded-md text-green-500 hover:bg-green-500/10 transition-colors"
                      title="Approve"
                    >
                      <Check className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => reviewMutation.mutate({ id: item.id, status: 'rejected' })}
                      disabled={reviewMutation.isPending}
                      className="p-1.5 rounded-md text-red-500 hover:bg-red-500/10 transition-colors"
                      title="Reject"
                    >
                      <XCircle className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
